import React, { useEffect, useMemo, useState } from "react";
import { IndianRupee, ShoppingBag, Users, Layers, Loader2 } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { api } from "../../lib/api";
import { compact, rupee } from "../../lib/format";
import { StatCard, Panel, DataTable, EntitySelect } from "./ui";
import { Header, Loading } from "./CorporateDashboard";

const GOLD = "#D4AF37";

export default function MallDashboard({ ctx, user }) {
  const malls = ctx.malls;
  const [mallId, setMallId] = useState(user.mall_id || malls[0]?.id);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!mallId) return;
    setData(null);
    api.get(`/dashboard/mall/${mallId}`).then((r) => setData(r.data));
  }, [mallId]);

  const selector = (
    <EntitySelect label="Mall" value={mallId} onChange={setMallId} testid="mall-select"
      options={malls.map((m) => ({ value: m.id, label: m.name }))} />
  );

  if (!data) return <div className="space-y-6"><Header title="Mall Dashboard" right={selector} /><Loading /></div>;
  const s = data.summary;

  return (
    <div className="space-y-6">
      <Header title={data.mall?.name || "Mall"} sub={`${data.mall?.city || ""} · today`} right={selector} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IndianRupee} label="Food Court Sales" value={compact(s.sales)} testid="stat-mall-sales" />
        <StatCard icon={ShoppingBag} label="Orders" value={s.orders} sub={`AOV ${rupee(s.aov)}`} accent="blue" />
        <StatCard icon={Users} label="Customers" value={s.customers} accent="emerald" />
        <StatCard icon={Layers} label="Food Courts" value={data.foodcourts.length} accent="gold" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Hourly Sales">
          <HourlyChart data={data.hourly} />
        </Panel>
        <Panel title="Zone Performance">
          <DataTable testid="mall-zone-table"
            columns={[
              { key: "zone", label: "Zone" },
              { key: "orders", label: "Orders" },
              { key: "sales", label: "Sales", render: (r) => <span className="gold-text font-semibold">{rupee(r.sales)}</span> },
            ]} rows={data.zones} />
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Food Courts">
          <DataTable testid="mall-fc-table"
            columns={[
              { key: "foodcourt", label: "Food Court" },
              { key: "orders", label: "Orders" },
              { key: "sales", label: "Sales", render: (r) => rupee(r.sales) },
            ]} rows={data.foodcourts} />
        </Panel>
        <Panel title="Category Mix">
          <DataTable testid="mall-cat-table"
            columns={[
              { key: "category", label: "Category" },
              { key: "qty", label: "Qty" },
              { key: "sales", label: "Sales", render: (r) => rupee(r.sales) },
            ]} rows={data.categories} />
        </Panel>
      </div>
    </div>
  );
}

export function HourlyChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}>
        <XAxis dataKey="hour" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} interval={1} />
        <YAxis hide />
        <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }} contentStyle={{ background: "#0E2650", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#fff" }} formatter={(v) => rupee(v)} />
        <Bar dataKey="sales" radius={[6, 6, 0, 0]} fill={GOLD} />
      </BarChart>
    </ResponsiveContainer>
  );
}
